/**
 * Fetches the active system announcement from Supabase and subscribes
 * to realtime changes so banners appear (and disappear) live.
 *
 * Dismissal is tracked in localStorage so each user only sees a given
 * announcement once until they explicitly dismiss it (or it is cleared).
 */
import { ref, computed } from 'vue'
import { supabase } from '@/supabase'
import type { RealtimeChannel } from '@supabase/supabase-js'

export interface SystemAnnouncement {
    id: string
    message: string
    color: 'yellow' | 'red'
    target_tabs: string[]
    is_active: boolean
    auto_clear_at: string | null
    created_at: string
    created_by: string
}

const DISMISSED_PREFIX = 'lh_dismissed_announcement_'

function isActiveAnnouncement(record: SystemAnnouncement | null | undefined): record is SystemAnnouncement {
    if (!record) return false
    if (!record.is_active) return false
    if (record.auto_clear_at && new Date(record.auto_clear_at) <= new Date()) return false
    return true
}

function isDismissed(id: string): boolean {
    try {
        return localStorage.getItem(`${DISMISSED_PREFIX}${id}`) === '1'
    } catch {
        return false
    }
}

function markDismissed(id: string): void {
    try {
        localStorage.setItem(`${DISMISSED_PREFIX}${id}`, '1')
    } catch { /* ignore */ }
}

// Module-level state so the composable is a singleton.
const announcement = ref<SystemAnnouncement | null>(null)
const loading = ref(false)
// Reactive set of IDs dismissed in this session (supplements localStorage).
const sessionDismissed = ref<Set<string>>(new Set())
let channel: RealtimeChannel | null = null
let started = false

function setAnnouncement(record: SystemAnnouncement | null): void {
    announcement.value = isActiveAnnouncement(record) ? record : null
}

async function fetchActive(): Promise<void> {
    loading.value = true
    try {
        const { data, error } = await supabase
            .from('system_announcements')
            .select('*')
            .eq('is_active', true)
            .order('created_at', { ascending: false })
            .limit(1)

        if (error) throw error

        setAnnouncement((data?.[0] as SystemAnnouncement | undefined) ?? null)
    } catch {
        // Non-critical; silently fail so we don't break the product.
        announcement.value = null
    } finally {
        loading.value = false
    }
}

function handleRealtimePayload(payload: {
    eventType: string
    new: Record<string, unknown>
    old: Record<string, unknown>
}): void {
    const { eventType, new: newRecord, old: oldRecord } = payload

    if (eventType === 'INSERT') {
        const record = newRecord as SystemAnnouncement
        if (isActiveAnnouncement(record)) {
            setAnnouncement(record)
        }
        return
    }

    if (eventType === 'UPDATE') {
        const record = newRecord as SystemAnnouncement
        if (isActiveAnnouncement(record)) {
            setAnnouncement(record)
            return
        }
        // Cleared or expired — hide immediately if this was the visible banner.
        if (announcement.value?.id === record.id) {
            announcement.value = null
        }
        return
    }

    if (eventType === 'DELETE') {
        const record = oldRecord as SystemAnnouncement
        if (announcement.value?.id === record?.id) {
            announcement.value = null
        }
        void fetchActive()
    }
}

function startRealtimeSubscription(): void {
    if (channel) return

    channel = supabase
        .channel('system_announcements_global')
        .on(
            'postgres_changes',
            {
                event: '*',
                schema: 'public',
                table: 'system_announcements',
            },
            (payload) => {
                handleRealtimePayload(payload)
            },
        )
        .subscribe()
}

function stopRealtimeSubscription(): void {
    if (channel) {
        supabase.removeChannel(channel)
        channel = null
    }
}

export function startSystemAnnouncementListener(): void {
    if (started) return
    started = true
    fetchActive()
    startRealtimeSubscription()
}

export function stopSystemAnnouncementListener(): void {
    stopRealtimeSubscription()
    announcement.value = null
    started = false
}

export default function useSystemAnnouncement() {
    /**
     * The banner is visible when:
     *   1. An active, non-expired announcement exists
     *   2. The user hasn't dismissed it this session/ever
     */
    const visibleAnnouncement = computed<SystemAnnouncement | null>(() => {
        // Access sessionDismissed.value so Vue tracks it as a dependency.
        const dismissed = sessionDismissed.value
        const a = announcement.value
        if (!a) return null
        if (!isActiveAnnouncement(a)) return null
        if (dismissed.has(a.id) || isDismissed(a.id)) return null
        return a
    })

    function dismiss(): void {
        const a = announcement.value
        if (!a) return
        markDismissed(a.id)
        sessionDismissed.value = new Set([...sessionDismissed.value, a.id])
    }

    return {
        visibleAnnouncement,
        loading,
        dismiss,
    }
}
